import { tradePromoOptimizationUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { TPODataContext } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    formatNumberWithCommas,
    generateErrorMsg,
    getConfig,
    removePropertyByKeyName,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import _ from 'lodash';
import InfiniteScroller from '../InfiniteScroller';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useFetchToolsLibraryData } from 'hooks/tradePromoOptimization/plans/useFetchToolsLibraryData';
import { getPromoLibraryTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import Footer from '../PricingSimulator/Footer';
import KPISelector from './AddEventModal/PromoSections/KPISelector';
import { Button } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import { PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import TableErrorBox from '../ErrorBox/TableErrorBox';
import { initialEditModalState } from './PromoLibraryContainer';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import EditToolsMetricsModal from './Modals/EditToolsMetricsModal';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { queryClient } from 'services/reactQueryService';
import { ToolTabKeys } from 'components/organism/TradePromoOptimization/Tools';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import NewGlobalFilter from '../common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from '../PricingSimulator/FilterRibbon/FilterRibbon';
import { Download } from 'assets/svg';

const productConstraintsTableId = 'product-constraints-table-id';

const ProductConstraintsContainer = () => {
    const {
        appConfig,
        exportPromoLibraryOptions: { exportToolsData },
    } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const promoLibraryExportThreshold = tpoConfig?.promoLibraryExportThreshold;

    const {
        dimensionConfigData,
        availableFilters,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        applyFilterObject,
        resetFilterObject,
        uiDimensionConfigData,
        filterUIDimensionsLoading,
    } = useContext(TPODataContext);

    const [productConstraintsTableVariable, setProductConstraintsTableVariable] = useState<any>({
        limit: tpoConfig?.tableLimit,
        offset: 0,
        search_key: '',
        kpis: [],
        library_group: ToolTabKeys.ProductConstraints,
        filters: {},
    });

    useEffect(() => {
        const updatedFilters = transformFiltersV2(applyFilterObject, uiDimensionConfigData?.filterKeys ?? []);

        setProductConstraintsTableVariable((prev: any) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [applyFilterObject]);

    const [openEditModal, setOpenEditModal] = useState({ ...initialEditModalState });

    const [productConstraintsKpiConfigData, setProductConstraintsKpiConfigData] =
        useState<PromoLibraryKpiConfigType | null>(null);

    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const promoLibraryAttributeTypes = tpoConfig?.promoLibraryAttributeTypes;

    const tableHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: productConstraintsTableId,
        height: tableHeight.current,
        buffer: 110, //footer
        //triggerDummyRender: true,
    });

    //GET  PRODUCT CONSTRAINTS KPI CONFIG DATA FROM BACKEND
    const { loading: productConstraintsKPIConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {},
        queryKey: ['tpo-product-constraint-kpi-config'],
        url: `${tradePromoOptimizationUrls.getPromoLibraryKpiUrl}/product_constraint`,
        variables: {},
        parserFunction(data: any) {
            const res: PromoLibraryKpiConfigType = {};
            data?.forEach((item: any) => {
                res[item?.kpi_key] = {
                    pre: item?.pre,
                    post: item?.post,
                    label: item?.kpi_name,
                    allowNegative: item?.allow_negative,
                    isEditable: item?.is_editable,
                    maxValue: item?.max_value,
                    minValue: item?.min_value,
                    type: item?.kpi_type,
                    storageType: item?.storage_type,
                    allowedDecimals: item?.allowed_decimals,
                    allowedValues: item?.allowed_values,
                };
            });
            setProductConstraintsKpiConfigData(res);
            return {};
        },
    });

    //product constraints data
    const {
        loading: productConstraintsTableDataLoading,
        error: productConstraintsTableDataError,
        data: productConstraintsTableData,
        count: productConstraintsTableDataCount,
    } = useFetchToolsLibraryData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                productConstraintsTableVariable?.kpis?.length > 0 &&
                    Object.keys(productConstraintsTableVariable?.filters)?.length > 0 &&
                    uiDimensionConfigData?.filterKeys?.length > 0,
            ),
        },
        url: tradePromoOptimizationUrls.getPromoLibraryTableDataUrl,
        queryKey: ['tpo-promo-library-data', 'tpo-product-constraint-table-data', productConstraintsTableVariable],
        variables: { ...(productConstraintsTableVariable || {}) },
        kpiConfigData: productConstraintsKpiConfigData,
        attributeTypes: promoLibraryAttributeTypes,
    });
    const commonLoading =
        filterUIDimensionsLoading || productConstraintsKPIConfigLoading || productConstraintsTableDataLoading;

    const { mutate: updateProductConstraint, loading: updateProductConstraintLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.updatePromoLibraryUrl,
        method: 'post',
    });

    function onEdit(
        from: 'cell' | 'column',
        kpiObject: PromoLibraryKpiConfigType,
        value?: any,
        rowId?: number,
        rowLabel?: string,
    ) {
        setOpenEditModal({
            kpiData: {
                kpiObject,
                value: from === 'column' ? undefined : value,
                rowId: from === 'column' ? undefined : rowId,
                rowLabel: from === 'column' ? undefined : rowLabel,
            },
            open: true,
        });
    }

    const columns = useMemo(() => {
        if (!productConstraintsTableData) return [];
        const tempColumns = kpiFilterDropdownValues
            ?.filter((item) => item.isSelected)
            ?.map((item) => {
                return {
                    label: item?.label,
                    key: item?.label,
                    kpiInfo: item,
                    sortKey: item?.storageType === 'range' ? false : item?.value,
                };
            });
        const memoisedColumns = getPromoLibraryTableColumns(promoLibraryAttributeTypes, tempColumns, onEdit, {
            sortBy: productConstraintsTableVariable?.sort_by,
            sortOrder: productConstraintsTableVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
        });
        return memoisedColumns;
    }, [productConstraintsTableData]);

    const isErrorStatus = productConstraintsTableDataError;
    const footerFirstText = `${
        'Showing ' +
        Number(productConstraintsTableData?.length ?? 0)?.toLocaleString() +
        ' of ' +
        formatNumberWithCommas(productConstraintsTableDataCount, 0) +
        ' records'
    }`;
    const footerSecondText = '';
    const footerThirdText = '';
    const footerFourthText = '';

    useEffect(() => {
        const valueList: any[] = [];
        const updatedList = Object.keys(productConstraintsKpiConfigData ?? {})?.map((configKey) => {
            valueList.push({
                key: configKey,
                label: productConstraintsKpiConfigData?.[configKey]['label'],
                storage_type: productConstraintsKpiConfigData?.[configKey]['storageType'],
            });
            return {
                ...productConstraintsKpiConfigData?.[configKey],
                label: productConstraintsKpiConfigData?.[configKey]['label'],
                value: configKey,
                isSelected: true,
                pre: productConstraintsKpiConfigData?.[configKey]['pre'],
                post: productConstraintsKpiConfigData?.[configKey]['post'],
            };
        });
        setProductConstraintsTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                kpis: valueList,
                sort_by: promoLibraryAttributeTypes?.[0]?.sortKey,
                sort_order: 'asc',
            };
        });
        setKpiDropdownValues(updatedList);
    }, [productConstraintsKpiConfigData]);

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV2(
                tempApplyFilterObject,
                uiDimensionConfigData.filterKeys,
                selectedFilter.value,
            );

            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                library_group: ToolTabKeys.ProductConstraints,
            };
        }
    }, [selectedFilter]);

    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = productConstraintsTableVariable.offset + productConstraintsTableVariable.limit;
        setProductConstraintsTableVariable({ ...productConstraintsTableVariable, offset: newOffset });
    }

    function exportProductConstraintsLibrary() {
        if (productConstraintsTableDataCount > promoLibraryExportThreshold) {
            return sendNotification(
                'error',
                `The data exceeds the export limit of ${formatNumberWithCommas(
                    promoLibraryExportThreshold,
                    0,
                )} records. Please apply more filters to reduce the data before proceeding with the export.
            `,
                10,
                500,
            );
        }
        sendNotification('info', tpoNotificationMessages.PLAN_EXPORT_QUEUE, 7, 500);
        const variables = {
            ...productConstraintsTableVariable,
            limit: isNaN(productConstraintsTableDataCount)
                ? promoLibraryExportThreshold
                : productConstraintsTableDataCount,
            default_headers: promoLibraryAttributeTypes?.map((item: any) => ({
                key: item?.key,
                label: item?.label,
            })),
        };
        exportToolsData(variables, `product_constraints.csv`);
    }

    return (
        <div className="relative">
            <div className="flex justify-between mt-12 mb-2 items-center">
                <div className="flex items-center gap-2"></div>

                <div className="flex justify-end gap-2 items-center">
                    {/* <Legend className="legend-size-square" data={legendItems} /> */}
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getAttributeNames}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={uiDimensionConfigData?.filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            const updatedValues = kpiFilterDropdownValues
                                ?.filter((kpiItem) => {
                                    return values.includes(kpiItem?.value);
                                })
                                .map((item) => ({ key: item?.value, storage_type: item?.storageType }));
                            setProductConstraintsTableVariable({
                                ...productConstraintsTableVariable,
                                offset: 0,
                                kpis: updatedValues,
                                sort_by:
                                    updatedValues?.find(
                                        (item) => item?.key === productConstraintsTableVariable?.sort_by,
                                    )?.key ?? promoLibraryAttributeTypes?.[0]?.sortKey,
                                sort_order: productConstraintsTableVariable?.sort_order ?? 'asc',
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                    {productConstraintsTableData?.length > 0 && (
                        <span
                            className="cursor-pointer"
                            onClick={() => {
                                exportProductConstraintsLibrary();
                            }}
                        >
                            <Download />
                        </span>
                    )}
                </div>
            </div>
            {/*3.  Filter Ribbon  */}
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setAllCheckedAttribute(obj.allCheckedAttribute);
                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>
            <InfiniteScroller
                data={productConstraintsTableData}
                loading={commonLoading}
                hasMore={productConstraintsTableData?.length < (productConstraintsTableDataCount || 0) || commonLoading}
                height={
                    productConstraintsTableData?.length && !productConstraintsTableDataError
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                } //height={200}
                //     loader={true}
                onNext={() => {
                    infiniteScrollOnNext(commonLoading);
                }}
            >
                <CustomTable
                    bordered
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };
                            setProductConstraintsTableVariable({
                                ...productConstraintsTableVariable,
                                sort_by: sort?.column?.sortKey,
                                sort_order: sortMapping?.[sort?.order] || 'asc',
                                offset: 0,
                            });
                        }
                    }}
                    showSorterTooltip={false}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={productConstraintsTableId}
                    columns={columns}
                    // loading={productConstraintsTableDataLoading}
                    scroll={{ x: '100%' }}
                    dataSource={productConstraintsTableData}
                    pagination={false}
                    style={{
                        opacity: productConstraintsTableDataLoading ? '0.3' : 1,
                        pointerEvents: productConstraintsTableDataLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: commonLoading ? (
                            <></>
                        ) : productConstraintsTableDataError ? (
                            <TableErrorBox error={productConstraintsTableDataError} height={tableHeight.current} />
                        ) : (
                            ''
                        ),
                    }}
                />
            </InfiniteScroller>
            <Footer
                type={isErrorStatus ? 'error' : 'default'}
                items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
            />
            <EditToolsMetricsModal
                updatePromoLibraryLoading={updateProductConstraintLoading}
                data={openEditModal.kpiData}
                open={openEditModal.open}
                onSubmit={(payload: any) => {
                    const updatedPayload = {
                        ...(payload ?? {}),
                        library_group: ToolTabKeys.ProductConstraints,
                        filters: productConstraintsTableVariable.filters,
                    };
                    //first clear the variables so that on success fresh api can be triggered
                    updateProductConstraint(updatedPayload, {
                        onSuccess: (data: any) => {
                            //on success, REMOVE THE CACHE
                            setProductConstraintsTableVariable({ ...productConstraintsTableVariable, offset: 0 });
                            sendNotification('success', tpoNotificationMessages.EDIT_PROMO_LIBRARY_SUCCESS);

                            queryClient.removeQueries({
                                exact: false,
                                queryKey: ['tpo-promo-library-data', 'tpo-product-constraint-table-data'],
                            });
                            setOpenEditModal({ ...initialEditModalState });
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            setOpenEditModal({ ...initialEditModalState });
                        },
                    });
                }}
                onCancel={() => {
                    setOpenEditModal({ ...initialEditModalState });
                }}
            />
        </div>
    );
};

export default ProductConstraintsContainer;
